import React, { useMemo, useState } from 'react';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { AssetExplorerConfigProvider, useResolvedAssetExplorerConfig } from 'components/AssetExplorer/AssetExplorerConfig';
import { PublishedAssetPickerDialog, PublishedAssetPickerResult } from 'components/TemplateEditor/EmailTemplate/EmailBuilder/blocks/PublishedAssetPickerDialog';
import { useModulePermissions } from 'hooks/useModulePermissions';
import type { Asset } from 'components/AssetExplorer/AssetExplorer.types';
import { ReelMediaElement, ReelMediaKind, ReelMediaSource } from '../reelCanvas.types';

export interface ReelMediaSelection {
    assetId: string;
    assetUrl: string;
    publishedRelativeUrl: string | null;
    assetName: string;
    mediaKind: ReelMediaKind;
    mimeType: string | null;
    source: ReelMediaSource;
}

export interface ReelMediaPickerProps {
    open: boolean;
    /** Library shown first (defaults to the last used, else Beam on Screen). */
    initialSource?: ReelMediaSource | null;
    /** Explorer asset types offered (default image + video). */
    assetTypes?: Asset['type'][];
    title?: string;
    onCancel: () => void;
    onSelect: (selection: ReelMediaSelection) => void;
}

/** Explorer route prefixes on st-ck-server (see BeamOnScreenAssetsRoutes / AssetExplorerRoutes). */
const SOURCE_BASE_PATH: Record<ReelMediaSource, string> = { bos: '/bos', ae: '/ae' };

/** Maps a picked explorer asset to the fields stored on a media element. */
export const selectionToMediaPatch = (sel: ReelMediaSelection): Partial<ReelMediaElement> => ({
    assetId: sel.assetId,
    assetUrl: sel.assetUrl,
    publishedRelativeUrl: sel.publishedRelativeUrl,
    assetName: sel.assetName,
    mediaKind: sel.mediaKind,
    mimeType: sel.mimeType,
    source: sel.source,
});

/**
 * Published IMAGE / VIDEO picker for reel media components. Wraps the explorer's published-asset
 * dialog and lets the user switch between the Beam on Screen library (`/bos`) and Asset
 * Management (`/ae`) when the company has both.
 */
export const ReelMediaPicker: React.FC<ReelMediaPickerProps> = ({
    open,
    initialSource,
    assetTypes = ['IMAGE', 'VIDEO'],
    title = 'Select published image or video',
    onCancel,
    onSelect,
}) => {
    const { hasAssetManagementAccess, hasBeamonScreenAccess, isUserSuperAdmin } = useModulePermissions();
    const canUseAe = hasAssetManagementAccess() || isUserSuperAdmin;
    const canUseBos = hasBeamonScreenAccess() || isUserSuperAdmin;
    const [source, setSource] = useState<ReelMediaSource>(initialSource ?? (canUseBos ? 'bos' : 'ae'));

    const config = useResolvedAssetExplorerConfig(
        useMemo(
            () => ({
                apiBasePath: SOURCE_BASE_PATH[source],
                title: source === 'bos' ? 'Assets' : 'Asset Explorer',
                uiStateStorageKey: source === 'bos' ? 'beamonscreen-assets-ui-state' : undefined,
            }),
            [source],
        ),
    );

    const headerActions =
        canUseAe && canUseBos ? (
            <ToggleButtonGroup
                size="small"
                exclusive
                value={source}
                onChange={(_e, v) => v && setSource(v as ReelMediaSource)}
                aria-label="Asset library"
                sx={{ '& .MuiToggleButton-root': { px: 1.25, py: 0.25, textTransform: 'none' } }}
            >
                <ToggleButton value="bos">
                    <Tooltip title="Beam on Screen → Assets">
                        <span>Beam on Screen</span>
                    </Tooltip>
                </ToggleButton>
                <ToggleButton value="ae">
                    <Tooltip title="Asset Management → Asset Explorer">
                        <span>Asset Management</span>
                    </Tooltip>
                </ToggleButton>
            </ToggleButtonGroup>
        ) : null;

    const handleConfirm = (result: PublishedAssetPickerResult) => {
        const { asset, url } = result;
        const kind: ReelMediaKind = asset.type === 'VIDEO' ? 'video' : asset.type === 'AUDIO' ? 'audio' : 'image';
        onSelect({
            assetId: asset._id,
            assetUrl: url,
            publishedRelativeUrl: asset.publishedRelativeUrl ?? null,
            assetName: asset.name,
            mediaKind: kind,
            mimeType: asset.mimeType ?? null,
            source,
        });
    };

    return (
        <AssetExplorerConfigProvider config={config}>
            <PublishedAssetPickerDialog
                open={open}
                assetTypeFilter={assetTypes}
                title={title}
                primaryActionLabel={assetTypes.length === 1 && assetTypes[0] === 'AUDIO' ? 'Use audio' : 'Use media'}
                headerActions={headerActions}
                onCancel={onCancel}
                onConfirm={handleConfirm}
            />
        </AssetExplorerConfigProvider>
    );
};

export default ReelMediaPicker;
